import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "../lib/motion";
import { scrollToSection } from "../lib/useSmoothScroll";
import { SOCIALS, LEGAL_LINKS } from "../data/site";
import logoLight from "../assets/logo/logo-light.webp";
import waveHero from "../assets/decor/wave-hero.png";
import "./Footer.css";

const SOCIAL_ICONS = {
  Instagram: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="17.4" cy="6.6" r="1.2" fill="currentColor" />
    </>
  ),
  Youtube: (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="4" stroke="currentColor" strokeWidth="1.7" />
      <path d="M10.5 9.6v4.8l4.2-2.4-4.2-2.4Z" fill="currentColor" />
    </>
  ),
};

export default function Footer() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-footer-item]",
        { autoAlpha: 0, y: 26 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.09,
          scrollTrigger: { trigger: root, start: "top 85%", once: true },
        }
      );
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <footer className="footer" ref={rootRef}>
      <div
        className="footer__wave"
        style={{ backgroundImage: `url(${waveHero})` }}
        aria-hidden="true"
      />
      <div className="footer__glow" aria-hidden="true" />

      <div className="container footer__inner">
        <div className="footer__brand" data-footer-item>
          <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection("#home"); }} aria-label="Private Stays — back to top">
            <img className="footer__logo" src={logoLight} alt="Private Stays" width="601" height="165" />
          </a>
          <p className="footer__about">
            <strong>Your Jordanian Adventure Awaits:</strong> Private Stays connects you with the
            best of Jordan&rsquo;s accommodations.
          </p>
          <ul className="footer__socials">
            {SOCIALS.map((s) => (
              <li key={s.label}>
                <a href={s.href} target="_blank" rel="noreferrer noopener" aria-label={s.label}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    {SOCIAL_ICONS[s.label]}
                  </svg>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <nav className="footer__links" aria-label="Legal" data-footer-item>
          <h2 className="footer__heading">Links</h2>
          <ul>
            {LEGAL_LINKS.map((l) => (
              <li key={l.label}>
                {l.href ? (
                  <a
                    href={l.href}
                    target={l.href.startsWith("http") ? "_blank" : undefined}
                    rel={l.href.startsWith("http") ? "noreferrer noopener" : undefined}
                  >
                    <span>{l.label}</span>
                  </a>
                ) : (
                  <span className="footer__links-text">{l.label}</span>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="footer__bar">
        <div className="container footer__bar-inner">
          <p>Private Stays</p>
          <p>Copyright &copy; 2023. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
}
